import { useEffect, useMemo, useState } from 'react';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DoneOutlineIcon from '@mui/icons-material/DoneOutline';
import DangerousIcon from '@mui/icons-material/Dangerous';
import RemoveIcon from '@mui/icons-material/Remove';
import PauseIcon from '@mui/icons-material/Pause';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

//MRT Imports
import {
  MaterialReactTable,
  MRT_CopyButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
  useMaterialReactTable,
} from 'material-react-table';

//Material UI Imports
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Typography
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { ClearIcon } from '@mui/x-date-pickers';
import { useOrganizationSelect } from '../../context/OrganizationSelectContext';
import { useSelector } from 'react-redux';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { mkConfig, generateCsv, download } from 'export-to-csv';

const Users = () => {

    const [filteredUsers, setfilteredUsers] = useState([]);
    const { data: users = [], loading, error } = useSelector((state) => state.users.users);
  
    const { selectedOrganization } = useOrganizationSelect();

    const exportColumns = [
      'displayName',
      'userPrincipalName',
      'organizationName',
      'assignedLicenses',
      'created',
      'vboDetails',
      'status'
    ];


  useEffect(() => {
    if (selectedOrganization) {
      setfilteredUsers(users.filter(
        (item) => item.organizationName === selectedOrganization
      ));
    } else {
      setfilteredUsers(users);
    }
  }, [selectedOrganization, users]);


  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

    const columns = useMemo(
      () => [
        {
          id: 'users',
          columns: [
            {
              accessorKey: 'firstname',
              header: 'Prénom',
              size: 150,
              filterVariant: 'autocomplete',
            },
            {
              accessorKey: 'lastname',
              header: 'Nom',
              size: 150,
              filterVariant: 'autocomplete',
            },
            {
              accessorKey: 'email',
              header: 'Email',
              size: 200,
              filterVariant: 'autocomplete',
            },
             {
              accessorFn: (row) => row.organizationDetails?.name || '',
              id: 'organizationName',
              header: 'Client',
              size: 200,
              filterVariant: 'autocomplete',
            },
            {
              accessorKey: 'jobTitle',
              header: 'Poste',
              size: 150,
              filterVariant: 'autocomplete',
            },
            {
              accessorFn: (row) =>
                Array.isArray(row.phones) && row.phones.length > 0
                  ? row.phones.join(', ')
                  : '',
              id: 'phones',
              header: 'Téléphones',
              size: 200,
              Cell: ({ row }) =>
                Array.isArray(row.original.phones) && row.original.phones.length > 0
                  ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                      {row.original.phones.map((phone, idx) => (
                        <Typography key={idx} variant="body2">{phone}</Typography>
                      ))}
                    </Box>
                  )
                  : <Typography variant="body2" color="textSecondary">Aucun</Typography>,
              filterVariant: 'autocomplete',
            },
            {
              accessorFn: (row) =>
                Array.isArray(row.emails) && row.emails.length > 0
                  ? row.emails.join(', ')
                  : '',
              id: 'emails',
              header: 'Emails secondaires',
              size: 200,
              Cell: ({ row }) =>
                Array.isArray(row.original.emails) && row.original.emails.length > 0
                  ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                      {row.original.emails.map((email, idx) => (
                        <Typography key={idx} variant="body2">{email}</Typography>
                      ))}
                    </Box>
                  )
                  : <Typography variant="body2" color="textSecondary">Aucun</Typography>,
              filterVariant: 'autocomplete',
            },
             {
              accessorFn: (row) => row.role,
              id: 'role',
              header: 'Role',
              size: 100,
              Cell: ({ row }) => (
                row.original.role === "admin"
                  ? <Chip label="Admin" color="primary" size="small" />
                  : <Chip label="Client" color="secondary" size="small" />
              ),
              filterVariant: 'autocomplete',
            },
              {
              accessorFn: (row) => row.applicationAccess ? "Oui" : "Non",
              id: 'applicationAccess',
              header: 'Accès',
              size: 40,
              Cell: ({ row }) => (
                row.original.applicationAccess 
                  ? <CheckCircleIcon color='primary' />
                  : <HighlightOffIcon />
              ),
              filterVariant: 'autocomplete',
            },
            {
              accessorFn: (row) => row.pipedrive_id ? 'Oui' : 'Non',
              id: 'pipedrive',
              header: 'Pipedrive',
              size: 40,
              Cell: ({ row }) => (
                row.original.pipedrive_id
                    ? <CheckCircleIcon color='primary' />
                  : <HighlightOffIcon />
              ),
              filterVariant: 'autocomplete',
            },
            {
              accessorFn: (row) => row.o365user_id ? 'Oui' : 'Non',
              id: 'o365',
              header: 'O365',
              size: 40,
              Cell: ({ row }) => (
                row.original.o365user_id
                   ? <CheckCircleIcon color='primary' />
                  : <HighlightOffIcon />
              ),
              filterVariant: 'autocomplete',
            },
           
          ],
        },
      ],
      [],
    );
    
  
    const table = useMaterialReactTable({
      columns,
      data: filteredUsers ? filteredUsers : null, //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
      enableColumnFilterModes: true,
      enableColumnOrdering: true,
      enableGrouping: true,
      enableColumnPinning: true,
      enableFacetedValues: true,
      // enableRowSelection: true,
      initialState: {
        density:"compact",
        // showColumnFilters: true,
        // showGlobalFilter: true,
        columnPinning: {
          left: ['mrt-row-expand', 'mrt-row-select'],
          right: ['mrt-row-actions'],
        },
        pagination: {
          pageSize: 50,

        }
      },
      paginationDisplayMode: 'pages',
      positionToolbarAlertBanner: 'bottom',
      enableRowSelection: true,
      muiSearchTextFieldProps: {
        size: 'small',
        variant: 'outlined',
      },
      muiPaginationProps: {
        color: 'secondary',
        rowsPerPageOptions: [50, 100, 150],
        shape: 'rounded',
        variant: 'outlined',
      },
    });
  
  
    return (
      <Box>
        <Typography variant="h6" my={1} mb={3}>Utilisateurs</Typography>
        
        {loading ? (
          <Skeleton variant="rectangular" width="100%" height={600} />
        ) : users.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h6" color="textSecondary">
              Aucune donnée disponible
            </Typography>
          </Box>
        ) : (
          <MaterialReactTable table={table} />
        )}
      </Box>
    );


};

export default Users;
